import { trpc } from './trpc';
import { inferRouterOutputs, inferRouterInputs } from '@trpc/server';
import { AppRouter } from '@tagadapay/trpc-types';
import { brandingConfig } from '@/config/branding';
import { useToast } from '@/components/ui/use-toast';

type OutputRouter = inferRouterOutputs<AppRouter>;
type InputRouter = inferRouterInputs<AppRouter>;

/**
 * Merchant-facing guide for pointing a custom domain at checkout (CRM-069).
 * Null on white-label partner hosts (no TagadaPay help center to link to).
 */
const DOMAIN_SETUP_GUIDE = brandingConfig.helpCenterUrl
  ? `${brandingConfig.helpCenterUrl}/en/articles/60-add-a-custom-domain-to-checkout`
  : null;

// Output types
export type AddCustomDomainOutput = OutputRouter['domains']['addCustomDomain'];
export type VerifyCustomDomainOutput = OutputRouter['domains']['verifyCustomDomain'];
export type RemoveCustomDomainOutput = OutputRouter['domains']['removeCustomDomain'];
export type PreviewRemoveCustomDomainOutput = OutputRouter['domains']['previewRemoveCustomDomain'];
export type GetDnsLookupOutput = OutputRouter['domains']['getDnsLookup'];
export type GetDomainConfigOutput = OutputRouter['domains']['getDomainConfig'];
export type ListCustomDomainsOutput = OutputRouter['domains']['list'];
export type CustomDomainItem = ListCustomDomainsOutput['domains'][number];

// Input types
export type AddCustomDomainInput = InputRouter['domains']['addCustomDomain'];
export type VerifyCustomDomainInput = InputRouter['domains']['verifyCustomDomain'];
export type RemoveCustomDomainInput = InputRouter['domains']['removeCustomDomain'];
export type PreviewRemoveCustomDomainInput = InputRouter['domains']['previewRemoveCustomDomain'];
export type GetDnsLookupInput = InputRouter['domains']['getDnsLookup'];
export type GetDomainConfigInput = InputRouter['domains']['getDomainConfig'];

// Query hooks
export const useCustomDomainsList = (options?: {
  enabled?: boolean;
  refetchOnMount?: boolean | 'always';
  refetchOnWindowFocus?: boolean;
}) => {
  const { enabled = true, ...otherOptions } = options || {};
  return trpc.domains.list.useQuery(undefined, {
    enabled,
    ...otherOptions,
  });
};

export const useDnsLookup = (params: GetDnsLookupInput, enabled = true) => {
  return trpc.domains.getDnsLookup.useQuery(params, {
    enabled: enabled && !!params.domain,
  });
};

export const useDomainConfig = (params: GetDomainConfigInput, enabled = true) => {
  return trpc.domains.getDomainConfig.useQuery(params, {
    enabled: enabled && !!params.domain,
  });
};

export const usePreviewRemoveCustomDomain = (params: PreviewRemoveCustomDomainInput, enabled = true) => {
  return trpc.domains.previewRemoveCustomDomain.useQuery(params, {
    enabled: enabled && !!params.domainId,
  });
};

// Mutation hooks
export const useAddCustomDomain = () => {
  const { toast } = useToast();
  const utils = trpc.useUtils();

  return trpc.domains.addCustomDomain.useMutation({
    onSuccess: (data) => {
      if (!data.success) {
        toast({
          title: 'Error',
          description: data.error || 'Failed to add custom domain',
          variant: 'destructive',
        });
        return;
      }
      utils.domains.list.invalidate();
      toast({
        title: 'Success',
        description: 'Custom domain added successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to add custom domain',
        variant: 'destructive',
      });
    },
  });
};

export const useVerifyCustomDomain = () => {
  const { toast } = useToast();
  const utils = trpc.useUtils();

  return trpc.domains.verifyCustomDomain.useMutation({
    onSuccess: (data) => {
      utils.domains.list.invalidate();
      utils.domains.getDomainConfig.invalidate();

      if (data.status === 'Valid Configuration') {
        toast({
          title: 'Success',
          description: 'Domain verified successfully',
        });
        return;
      }

      // CRM-069: the opaque status ('Unknown Error' etc.) hides the real cause,
      // which the server already returns on domainJson/configJson.error. Surface
      // that concrete reason + a link to the DNS setup guide instead.
      const vercelError = (data.domainJson as any)?.error || (data.configJson as any)?.error;
      const cause = vercelError?.message
        ? `${vercelError.message}${vercelError.code ? ` (${vercelError.code})` : ''}`
        : `Status: ${data.status}`;
      const isFailure = data.status === 'Error' || data.status === 'Unknown Error' || !!vercelError;

      toast({
        title: isFailure ? 'Domain verification failed' : 'Verification pending',
        description: DOMAIN_SETUP_GUIDE ? `${cause}. Setup guide: ${DOMAIN_SETUP_GUIDE}` : cause,
        variant: isFailure ? 'destructive' : undefined,
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to verify custom domain',
        variant: 'destructive',
      });
    },
  });
};

export const useRemoveCustomDomain = () => {
  const { toast } = useToast();
  const utils = trpc.useUtils();

  return trpc.domains.removeCustomDomain.useMutation({
    onSuccess: (data) => {
      if (!data.success) {
        toast({
          title: 'Error',
          description: data.error || 'Failed to remove custom domain',
          variant: 'destructive',
        });
        return;
      }
      utils.domains.list.invalidate();
      toast({
        title: 'Success',
        description: 'Custom domain removed successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to remove custom domain',
        variant: 'destructive',
      });
    },
  });
};
